import React, { useState, useRef, useEffect, useCallback } from "react";
import { ChevronDown } from "./LpIcons";

/**
 * Lightweight custom select matching the prototype's `.csel` control.
 * options: [{ value, label }]. Controlled via `value` / `onChange(value)`.
 */
function LpSelect({ options = [], value, onChange, placeholder = "Select…", disabled = false, className = "", caretIcon = null }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) close();
    };
    const onKey = (e) => { if (e.key === "Escape") close(); };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, close]);

  const selected = options.find((o) => o.value === value);
  const cls = ["lp-select", open ? "lp-select--open" : "", className].filter(Boolean).join(" ");

  const handleSelect = (val) => {
    if (onChange) onChange(val);
    close();
  };

  return (
    <div className={cls} ref={rootRef}>
      <button
        type="button"
        className="lp-select__trigger"
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={selected ? "" : "lp-select__trigger--placeholder"}>
          {selected ? selected.label : placeholder}
        </span>
        {caretIcon || <ChevronDown className="lp-select__caret" width={14} height={14} />}
      </button>
      {open && (
        <ul className="lp-select__menu" role="listbox">
          {options.length === 0 && <li className="lp-select__empty">No options</li>}
          {options.map((opt) => (
            <li
              key={String(opt.value)}
              role="option"
              aria-selected={opt.value === value}
              className={["lp-select__option", opt.value === value ? "lp-select__option--selected" : ""].filter(Boolean).join(" ")}
              onClick={() => handleSelect(opt.value)}
            >
              {opt.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default React.memo(LpSelect);
